import {
  BadgeCheckIcon,
  BotIcon,
  CalendarRangeIcon,
  CrownIcon,
  EyeOffIcon,
  FlameIcon,
  GitBranchIcon,
  MapPinIcon,
  MessageCircleIcon,
  ThumbsUpIcon,
  TrophyIcon,
  TriangleAlertIcon,
  UserIcon,
  UserCheckIcon,
} from "lucide-react";

import { type SearchCandidateResult } from "../searchV2CandidateResult";
import { SearchV2LastReplyChip } from "./SearchV2LastReplyChip";
import { SearchV2SallyStateBadge } from "./SearchV2SallyStateBadge";
import { SearchV2SameCompanyBadge } from "./SearchV2SameCompanyBadge";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  candidacyStatusToLabel,
  candidacyStatusToPipelineStage,
  pipelineStageToKanbanToneClass,
  positionCategoryToLabel,
} from "@/lib/constants";
import { useGetJob } from "@/hooks/queries/useGetJob";
import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";

import { companies } from "@/lib/companies";
import { dates } from "@/lib/dates";
import { formatting } from "@/lib/formatting";
import { locations } from "@/lib/locations";
import { cn } from "@/lib/utils";
import {
  CandidacyStatus,
  LastPing,
  PipelineStage,
  QuoMessageDirection,
  SearchV2WorkExperience,
} from "@/types";

const getLastCommunicationText = (lastCommunication: LastPing) => {
  const communicationDate = new Date(lastCommunication.at);
  const today = new Date();
  if (dates.localDayKey(communicationDate) === dates.localDayKey(today)) {
    return dates.formatTimeShort(communicationDate);
  }
  if (
    dates.localDayKey(communicationDate) ===
    dates.localDayKey(dates.addLocalDays(today, -1))
  ) {
    return "Yesterday";
  }
  return dates.formatShortDate(lastCommunication.at);
};

const getRecentActivityText = (recentActivityAt: string) => {
  const activityDate = new Date(recentActivityAt);
  const today = new Date();
  if (dates.localDayKey(activityDate) === dates.localDayKey(today)) {
    return "Last Active Today";
  }
  if (
    dates.localDayKey(activityDate) ===
    dates.localDayKey(dates.addLocalDays(today, -1))
  ) {
    return "Last Active Yesterday";
  }
  return `Last Active ${dates.formatShortDate(recentActivityAt)}`;
};

interface Props {
  candidate: SearchCandidateResult;
  // Keyboard target: the hovered card, or the one the hotkeys advanced to.
  isActive?: boolean;
  isInterestedSelected: boolean;
  isPrioPending: boolean;
  isTopCandidate?: boolean;
  onHide: (talentId: number) => void;
  // Fires on real mouse movement only, so a programmatic scroll under a
  // stationary cursor doesn't steal the active card.
  onHoverChange?: (talentId: number) => void;
  // Reports which card the cursor left so the owner can clear only if that
  // card was the active one.
  onHoverLeave?: (talentId: number) => void;
  onInterestedToggle: (talentId: number) => void;
  onSelect: (candidate: SearchCandidateResult) => void;
  prioSummary?: string;
}
export const SearchV2CandidateCard = ({
  candidate,
  isActive = false,
  isInterestedSelected,
  isPrioPending,
  isTopCandidate = false,
  onHide,
  onHoverChange,
  onHoverLeave,
  onInterestedToggle,
  onSelect,
  prioSummary,
}: Props) => {
  /* Database CRUD Operations */
  // Read — same key as the job page, so a cache hit; only the category is used.
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const { data: job } = useGetJob({
    companyId: currentCompanyId,
    jobId: currentJobId,
  });

  /* Helpers */
  const candidateName = [candidate.firstName, candidate.lastName]
    .filter(Boolean)
    .join(" ");

  const activePipelines = candidate.activePipelines;
  const submissions = candidate.submissions;
  const candidateWorkExperiences = candidate.workExperiences;
  const workExperiences = candidateWorkExperiences.slice(0, 2);
  const matchedKeywords = [
    ...new Set(
      candidateWorkExperiences.flatMap(
        (workExperience) => workExperience.matchedKeywords ?? [],
      ),
    ),
  ];
  const matchedTitles = [
    ...new Set(
      candidateWorkExperiences.flatMap(
        (workExperience) => workExperience.matchedTitles ?? [],
      ),
    ),
  ];
  const recentActivityText = candidate.recentActivityAt
    ? getRecentActivityText(candidate.recentActivityAt)
    : null;
  const interviewPipelines = activePipelines.filter(
    (pipeline) =>
      candidacyStatusToPipelineStage[pipeline.status] ===
      PipelineStage.INTERVIEW,
  );
  // Only pipeline is this job's — the "In Pipeline" badge already covers it.
  const showActivePipelines =
    activePipelines.length > 0 &&
    !(
      activePipelines.length === 1 && activePipelines[0].jobId === currentJobId
    );

  /* Render */
  const renderTopCandidateChip = () => {
    if (!isTopCandidate) return null;

    return (
      <Row className="relative isolate h-5 w-fit shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-transparent bg-gradient-to-r from-blue-50 to-blue-100 px-2.5 ring-1 ring-blue-100 after:pointer-events-none after:absolute after:inset-0 after:-z-10 after:animate-[top-candidate-halo-pulse_2.5s_ease-in-out_infinite] after:rounded-full after:bg-blue-100/30 after:shadow-[0_0_0_6px_rgba(59,130,246,0.07),0_0_16px_rgba(59,130,246,0.14)] after:content-['']">
        <CrownIcon className="h-3 w-3 text-blue-500" />
        <Text className="whitespace-nowrap text-xs font-medium leading-none text-blue-700">
          Top Candidate
        </Text>
      </Row>
    );
  };

  const renderHeaderRow = () => {
    const isRejectedStatus = candidate.candidacyStatus
      ? [CandidacyStatus.REJECTED, CandidacyStatus.COULD_NOT_CONTACT].includes(
          candidate.candidacyStatus,
        )
      : false;
    return (
      <Row className="min-w-0 flex-wrap items-center gap-2">
        {candidate.permanentStatus ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Row className="h-6 w-6 items-center justify-center text-red-600">
                  <TriangleAlertIcon className="h-4 w-4 fill-red-100" />
                </Row>
              </TooltipTrigger>
              <TooltipContent className="max-w-72" side="top">
                <Text className="text-xs font-normal text-slate-700">
                  {candidate.permanentStatus}
                </Text>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
        <Button
          className="h-auto p-0 text-base font-semibold text-slate-950 hover:bg-transparent hover:text-blue-600 hover:underline"
          onClick={(event) => {
            event.stopPropagation();
            onSelect(candidate);
          }}
          type="button"
          variant="ghost"
        >
          {candidateName}
        </Button>
        {candidate.desiredPosition && (
          <Text className="text-sm font-normal text-slate-600">
            {candidate.desiredPosition}
          </Text>
        )}
        {renderTenureBadge()}
        {isRejectedStatus && (
          <Row className="h-5 shrink-0 items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2">
            <Text className="text-xs font-medium leading-none text-red-700">
              Prev rejected
            </Text>
          </Row>
        )}
        {renderSubmissions()}
        {renderInterviewBadge()}
        {renderSallyState()}
      </Row>
    );
  };

  // Sally's non-conversions, surfaced so a staffer can take a second run at
  // them — candidates who ignore the agent often answer a person. Sits in the
  // header because it's a fact about this candidate on this job, not a detail.
  const renderSallyState = () => {
    if (!candidate.sallyState) {
      return null;
    }
    return (
      <SearchV2SallyStateBadge
        reason={candidate.sallyStateReason}
        state={candidate.sallyState}
      />
    );
  };

  // Only tenure in the job's own category is worth a glance here; other
  // categories live in the profile.
  const renderTenureBadge = () => {
    const category = job?.positionCategory;
    if (!category) return null;
    const years = candidate.relevantTenure?.[category];
    if (typeof years !== "number" || years <= 0) return null;
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Row className="h-5 min-w-5 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 px-1.5">
              <Text className="text-[11px] font-medium leading-none text-slate-600">
                {formatting.years(years)}
              </Text>
            </Row>
          </TooltipTrigger>
          <TooltipContent side="top">
            <Text className="text-xs font-normal text-slate-700">
              {formatting.years(years)} in {positionCategoryToLabel[category]}
            </Text>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderInterviewBadge = () => {
    if (!interviewPipelines.length) {
      return null;
    }
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Row className="h-5 shrink-0 items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-2">
              <CalendarRangeIcon className="h-3 w-3 text-violet-700" />
              <Text className="text-xs font-medium leading-none text-violet-700">
                Interviewing
              </Text>
            </Row>
          </TooltipTrigger>
          <TooltipContent className="max-w-80" side="top">
            <Column className="gap-2 py-1">
              {interviewPipelines.map((pipeline) => (
                <Text
                  className="text-xs font-normal text-slate-700"
                  key={`${pipeline.title}-${pipeline.company}-${pipeline.status}`}
                >
                  {pipeline.title} · {pipeline.company}
                </Text>
              ))}
            </Column>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderSubmissions = () => {
    if (!submissions.length) {
      return null;
    }
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="h-auto p-0 text-sm font-normal text-slate-600 hover:bg-transparent hover:text-slate-900"
              type="button"
              variant="ghost"
            >
              <BadgeCheckIcon className="h-3.5 w-3.5 text-primary-900" />
              {submissions.length}{" "}
              {submissions.length === 1 ? "submission" : "submissions"}
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-96" side="top">
            <Column className="gap-2 py-1">
              {submissions.map((submission) => (
                <Row
                  className="items-center justify-between gap-5"
                  key={`${submission.title}-${submission.company}-${submission.submittedAt}`}
                >
                  <Text className="text-xs font-normal text-slate-700">
                    {submission.title} · {submission.company}
                  </Text>
                  <Text className="shrink-0 text-xs font-normal text-slate-500">
                    {dates.formatShortDate(submission.submittedAt)}
                  </Text>
                </Row>
              ))}
            </Column>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderActivePipelines = () => {
    if (!showActivePipelines) {
      return null;
    }

    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="h-auto shrink-0 p-0 text-sm font-normal text-primary-900 hover:bg-transparent hover:text-primary-700"
              type="button"
              variant="ghost"
            >
              {candidate.candidacyStatus ? (
                <Row className="h-5 shrink-0 items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2">
                  <GitBranchIcon className="h-3 w-3 text-emerald-700" />
                  <Text className="text-xs font-medium leading-none text-emerald-700">
                    In Pipeline
                    {activePipelines.length > 1
                      ? ` +${(activePipelines.length - 1).toString()} ${activePipelines.length - 1 === 1 ? "other" : "others"}`
                      : null}
                  </Text>
                </Row>
              ) : activePipelines.length > 0 ? (
                `${activePipelines.length.toString()} active ${activePipelines.length === 1 ? "pipeline" : "pipelines"}`
              ) : null}
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-80" side="top">
            <Column className="gap-2 py-1">
              {activePipelines.map((pipeline) => {
                const pipelineStage =
                  candidacyStatusToPipelineStage[pipeline.status];
                const isCurrentJob = pipeline.jobId === currentJobId;
                return (
                  <Row
                    className={cn(
                      "items-center justify-between gap-4",
                      isCurrentJob &&
                        "-mx-1.5 rounded-md bg-emerald-50 px-1.5 py-1 ring-1 ring-emerald-200",
                    )}
                    key={`${pipeline.title}-${pipeline.company}-${pipeline.status}`}
                  >
                    <Text
                      className={cn(
                        "text-xs font-normal text-slate-700",
                        isCurrentJob && "font-medium text-emerald-800",
                      )}
                    >
                      {pipeline.title} · {pipeline.company}
                    </Text>
                    <Row
                      className={cn(
                        "shrink-0 items-center rounded-full border border-slate-300/70 px-2 py-1",
                        pipelineStage &&
                          pipelineStageToKanbanToneClass[pipelineStage],
                      )}
                    >
                      <Text className="text-[11px] font-medium text-slate-700">
                        {pipeline.status in candidacyStatusToLabel
                          ? candidacyStatusToLabel[pipeline.status]
                          : "Unknown Status"}
                      </Text>
                    </Row>
                  </Row>
                );
              })}
            </Column>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderActivityRow = () => {
    if (
      !(
        candidate.candidacyStatus ??
        recentActivityText ??
        activePipelines.length > 0
      )
    ) {
      return null;
    }
    return (
      <Row className="items-center">
        <Row className="w-32 shrink-0 items-center gap-1.5">
          <FlameIcon className="h-3.5 w-3.5 text-orange-500" />
          <Text className="text-sm font-normal text-slate-500">Activity</Text>
        </Row>
        <Row className="min-w-0 items-center gap-2 whitespace-nowrap">
          {renderActivePipelines()}
          {showActivePipelines && recentActivityText && (
            <Text className="text-sm font-normal text-slate-400">·</Text>
          )}
          {recentActivityText && (
            <Text className="shrink-0 text-sm font-normal leading-5 text-slate-500">
              {recentActivityText}
            </Text>
          )}
        </Row>
      </Row>
    );
  };

  const renderRecentMessageItem = (message: LastPing) => {
    const isFromCandidate = message.direction === QuoMessageDirection.INCOMING;
    const getSenderLabel = () => {
      if (isFromCandidate) return "Candidate";
      if (message.sentByAgent) return "AI";
      return "Staffer";
    };
    return (
      <Column className="gap-0.5" key={message.at}>
        <Row className="items-center gap-1.5">
          {!isFromCandidate && message.sentByAgent ? (
            <BotIcon className="h-3 w-3 text-slate-500" />
          ) : (
            <UserIcon className="h-3 w-3 text-slate-500" />
          )}
          <Text className="text-xs font-medium text-slate-700">
            {getSenderLabel()}
          </Text>
          <Text className="ml-auto shrink-0 text-xs font-normal text-slate-500">
            {getLastCommunicationText(message)}
          </Text>
        </Row>
        {message.body && (
          <Text className="line-clamp-2 text-xs font-normal text-slate-500">
            {message.body}
          </Text>
        )}
      </Column>
    );
  };

  const renderLastCommsRow = () => {
    if (!candidate.lastCommunication) return null;
    const recentMessages = candidate.recentMessages ?? [];
    const lastInbound = candidate.lastInboundCommunication;
    // Newest first — last 2 messages all outgoing means candidate hasn't replied.
    // Fallback for hydration that predates lastInboundCommunication.
    const isUnresponsiveByRecentMessages =
      recentMessages.length >= 2 &&
      recentMessages
        .slice(0, 2)
        .every((message) => message.direction !== QuoMessageDirection.INCOMING);
    const row = (
      <Row className="w-fit items-center">
        <Row className="w-32 shrink-0 items-center gap-1.5">
          <MessageCircleIcon className="h-3.5 w-3.5 text-slate-400" />
          <Text className="text-sm font-normal text-slate-500">Last Comms</Text>
        </Row>
        <Row className="items-center gap-1.5">
          <Text className="text-sm font-normal text-slate-700">
            {getLastCommunicationText(candidate.lastCommunication)}
          </Text>
          {candidate.lastCommunication.sentByAgent ? (
            <BotIcon className="h-3.5 w-3.5 text-slate-500" />
          ) : (
            <UserIcon className="h-3.5 w-3.5 text-slate-500" />
          )}
          {lastInbound !== undefined ? (
            <SearchV2LastReplyChip
              lastCommunication={candidate.lastCommunication}
              lastInboundCommunication={lastInbound}
            />
          ) : null}
          {lastInbound === undefined && isUnresponsiveByRecentMessages ? (
            <Row className="h-5 shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2">
              <TriangleAlertIcon className="h-3 w-3 text-amber-600" />
              <Text className="text-xs font-medium leading-none text-amber-700">
                Not responding
              </Text>
            </Row>
          ) : null}
        </Row>
      </Row>
    );
    if (!recentMessages.length) return row;
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{row}</TooltipTrigger>
          <TooltipContent className="max-w-80" side="top">
            <Column className="gap-2.5 py-1">
              {recentMessages.map(renderRecentMessageItem)}
            </Column>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderDetailRows = () => {
    return (
      <>
        <Row className="items-center">
          <Row className="w-32 shrink-0 items-center gap-1.5">
            <MapPinIcon className="h-3.5 w-3.5 text-slate-400" />
            <Text className="text-sm font-normal text-slate-500">Location</Text>
          </Row>
          <Row className="items-center gap-1">
            {candidate.location && (
              <Text className="text-sm font-normal text-slate-500">
                {locations.stripCountrySuffix(candidate.location)}
              </Text>
            )}
          </Row>
        </Row>
        {candidate.lastCommunication && renderLastCommsRow()}
        {candidate.currentPlacement && (
          <Row className="items-center">
            <Row className="w-40 shrink-0 items-center gap-1.5">
              <UserCheckIcon className="h-3.5 w-3.5 text-green-600" />
              <Text className="text-sm font-normal text-slate-500">
                Current Placement
              </Text>
            </Row>
            <Text className="text-sm font-normal text-primary-900">
              {candidate.currentPlacement.title} ·{" "}
              {candidate.currentPlacement.company} ·{" "}
              {candidate.currentPlacement.tenure}
            </Text>
          </Row>
        )}
      </>
    );
  };

  const renderWorkExperience = () => {
    const workedAtCompanies = companies.extractEmployerNames(workExperiences);
    return (
      <Column className="gap-2">
        <Row className="items-center gap-2">
          <Text className="text-sm font-normal text-slate-500">
            Work Experience
          </Text>

          <SearchV2SameCompanyBadge
            workedAtCompanies={workedAtCompanies}
            isCurrentLaborupPlacement={!!candidate.currentPlacement}
          />
        </Row>
        <Column className="relative gap-2 pl-4 before:absolute before:inset-y-0 before:left-0 before:w-1 before:rounded-full before:bg-slate-200">
          {workExperiences.map(
            (workExperience: SearchV2WorkExperience, index: number) => (
              <Row
                className="items-center gap-1.5"
                key={`${String(candidate.userId)}-${String(index)}`}
              >
                <Text className="text-sm font-normal leading-5 text-slate-700">
                  {workExperience.title}
                </Text>
                <Text className="text-sm text-slate-400">•</Text>
                <Text className="text-xs font-normal text-slate-500">
                  {workExperience.company}
                </Text>
                <Text className="text-xs text-slate-400">•</Text>
                <Text className="text-xs font-normal text-slate-500">
                  {workExperience.years === 0
                    ? "<1 yr"
                    : `${String(workExperience.years)} yrs`}
                </Text>
                <Text className="text-xs font-normal text-slate-400">
                  {/* eslint-disable-next-line
                  @typescript-eslint/prefer-nullish-coalescing */}
                  ({workExperience.endYear?.trim() || "Present"})
                </Text>
              </Row>
            ),
          )}
          {workExperiences.length === 0 && (
            <Text className="text-sm font-normal text-slate-500">
              No Work experience provided
            </Text>
          )}
        </Column>
      </Column>
    );
  };

  const renderMatchChips = () => {
    if (!matchedTitles.length && !matchedKeywords.length) {
      return null;
    }
    return (
      <Row className="flex-wrap items-center gap-2">
        {matchedTitles.map((title) => (
          <Row
            className="h-7 items-center rounded-md bg-slate-100 px-3"
            key={`title-${title}`}
          >
            <Text className="text-xs font-normal leading-none text-slate-600">
              {title}
            </Text>
          </Row>
        ))}
        {matchedKeywords.map((keyword) => (
          <Row
            className="h-7 items-center rounded-md bg-slate-100 px-3"
            key={`keyword-${keyword}`}
          >
            <Text className="text-xs font-normal leading-none text-slate-600">
              {keyword}
            </Text>
          </Row>
        ))}
      </Row>
    );
  };

  const renderTopPrio = () => {
    if (!isPrioPending && prioSummary === undefined) {
      return null;
    }
    return (
      <Row className="items-top min-w-0 gap-2">
        <TrophyIcon className="h-3.5 w-3.5 shrink-0 animate-pulse text-amber-500 [animation-duration:5s]" />
        <Text className="font-regular shrink-0 text-xs text-slate-950">
          Customer Top Priority
        </Text>
        {isPrioPending && prioSummary === undefined ? (
          <Skeleton className="h-4 w-56" />
        ) : (
          <Text className="min-w-0 text-xs font-normal text-slate-500">
            {prioSummary}
          </Text>
        )}
      </Row>
    );
  };

  const renderActions = () => {
    return (
      <Column
        className="shrink-0 items-end gap-2"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <Row className="items-center gap-2">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="h-9 w-9 text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-700 group-hover/candidate:opacity-100"
                  onClick={(event) => {
                    event.stopPropagation();
                    onHide(candidate.talentId);
                  }}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <EyeOffIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <Text className="text-xs font-normal text-slate-700">
                  Hide from results
                </Text>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {candidate.schedulingAgentReachedOut ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Row className="h-9 w-9 items-center justify-center text-slate-400">
                    <BotIcon className="sally-icon-shimmer h-5 w-5 text-primary-700" />
                  </Row>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <Text className="text-xs font-normal text-slate-700">
                    Sally reached out
                  </Text>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
          {!isTopCandidate ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className={cn(
                      "h-9 w-9 border-0 p-0 text-blue-700 hover:bg-blue-50 hover:text-blue-800",
                      isInterestedSelected &&
                        "bg-primary-900 text-white hover:bg-primary-800 hover:text-white",
                    )}
                    onClick={(event) => {
                      event.stopPropagation();
                      onInterestedToggle(candidate.talentId);
                    }}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <ThumbsUpIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <Text className="text-xs font-normal text-slate-700">
                    Interested
                  </Text>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            renderTopCandidateChip()
          )}
        </Row>
      </Column>
    );
  };

  const render = () => {
    return (
      <Column
        className={cn(
          "group/candidate cursor-pointer gap-5 border-b border-slate-200 px-2 py-8 transition-[background-color,box-shadow] duration-300 last:border-b-0 hover:shadow-[0_8px_10px_-10px_rgba(15,23,42,0.3)]",
          isActive &&
            "bg-slate-50 shadow-[0_8px_10px_-10px_rgba(15,23,42,0.3)]",
        )}
        data-talent-id={candidate.talentId}
        onClick={() => {
          onSelect(candidate);
        }}
        onMouseMove={() => onHoverChange?.(candidate.talentId)}
        onMouseLeave={() => onHoverLeave?.(candidate.talentId)}
      >
        <Row className="items-start justify-between gap-5">
          {renderHeaderRow()}
          {renderActions()}
        </Row>
        <div className="-mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,28rem)_minmax(0,1fr)] lg:gap-x-5 2xl:gap-y-0">
          <Column className="min-w-0 gap-2.5 xl:col-start-1 xl:row-start-1">
            <div className="grid grid-cols-1 gap-y-2.5">
              {renderActivityRow()}
              {renderDetailRows()}
            </div>
          </Column>
          <Column className="min-w-0 2xl:col-start-2 2xl:row-start-1">
            {renderWorkExperience()}
          </Column>
        </div>
        {renderMatchChips()}
        {renderTopPrio()}
      </Column>
    );
  };

  return render();
};
